import type { Dispatch, SetStateAction } from 'react';
import { itineraryApi } from '../api/itinerary.api';
import { isAlreadyGone } from '../api/client';
import { applyNextDayUpdate } from './activityOrder';
import { removeActivitiesLocally } from './removeActivities';
import type { Activity, ItineraryDay } from '../types';

/**
 * The inverse of each itinerary action the history can undo (ADR-0015).
 *
 * An entry is built right after its action succeeded, from the state before and
 * after it. `undo`/`redo` talk to the server and write the result through the
 * history's own `setDays`, so the history knows the plan is still the one it
 * produced.
 */
export interface HistoryEntry {
  /** What the toast says the action did. */
  message: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  /**
   * A deferred write: what the action would have sent straight away. The
   * history sends it when the undo window closes; until then, undo is just
   * not sending it.
   */
  commit?: () => Promise<void>;
}

export interface EntryContext {
  tripId: string;
  setDays: Dispatch<SetStateAction<ItineraryDay[]>>;
  /** The plan as it is now, for a redo that has to diff against it. */
  getDays: () => ItineraryDay[];
}

export function stopIds(day: ItineraryDay | undefined): string[] {
  return day ? day.activities.filter(a => !a.isOrigin).map(a => a.id) : [];
}

function continuationsOf(days: ItineraryDay[]): Map<string, boolean> {
  return new Map(days.map(d => [d.id, d.startFromPrevStop]));
}

async function reload(ctx: EntryContext): Promise<void> {
  ctx.setDays(await itineraryApi.listDays(ctx.tripId));
}

async function reorder(ctx: EntryContext, dayId: string, orderedIds: string[]): Promise<void> {
  const { nextDayUpdate, activities } = await itineraryApi.reorderActivities(ctx.tripId, dayId, orderedIds);
  ctx.setDays(prev => {
    const updated = prev.map(d => (d.id === dayId ? { ...d, activities } : d));
    return nextDayUpdate ? applyNextDayUpdate(updated, nextDayUpdate) : updated;
  });
}

/**
 * The server switches a day's "continues from" off when the stop it copied
 * leaves the previous day. Putting the stop back does not switch it on again,
 * so the undo does — only for the days that had it on before the action.
 */
async function restoreContinuations(ctx: EntryContext, before: Map<string, boolean>): Promise<void> {
  const now = await itineraryApi.listDays(ctx.tripId);
  const lost = now.filter(d => before.get(d.id) && !d.startFromPrevStop);
  for (const day of lost) {
    await itineraryApi.updateDay(ctx.tripId, day.id, { startFromPrevStop: true });
  }
}

/** ADR-0010: the inverse of a reorder is a reorder. The hours are recomputed, never put back. */
export function reorderEntry(
  ctx: EntryContext, dayId: string, before: string[], after: string[], message: string,
): HistoryEntry {
  return {
    message,
    undo: () => reorder(ctx, dayId, before),
    redo: () => reorder(ctx, dayId, after),
  };
}

export function moveEntry(ctx: EntryContext, args: {
  activityIds: string[];
  targetDayId: string;
  /** The plan before the move. */
  before: ItineraryDay[];
  /** The target day's stop order the move ended with. */
  targetAfter: string[];
  message: string;
}): HistoryEntry {
  const { activityIds, targetDayId, before, targetAfter, message } = args;
  const bySource = new Map<string, string[]>();
  for (const day of before) {
    if (day.id === targetDayId) continue;
    const here = day.activities.filter(a => activityIds.includes(a.id)).map(a => a.id);
    if (here.length > 0) bySource.set(day.id, here);
  }
  const continuations = continuationsOf(before);

  return {
    message,
    undo: async () => {
      for (const [dayId, ids] of bySource) {
        await itineraryApi.moveActivities(ctx.tripId, ids, dayId);
        // The server appends the returning cards; the old order puts them back in their slot.
        await itineraryApi.reorderActivities(ctx.tripId, dayId, stopIds(before.find(d => d.id === dayId)));
      }
      await itineraryApi.reorderActivities(ctx.tripId, targetDayId, stopIds(before.find(d => d.id === targetDayId)));
      await restoreContinuations(ctx, continuations);
      await reload(ctx);
    },
    redo: async () => {
      await itineraryApi.moveActivities(ctx.tripId, activityIds, targetDayId);
      await itineraryApi.reorderActivities(ctx.tripId, targetDayId, targetAfter);
      await reload(ctx);
    },
  };
}

export function duplicateEntry(ctx: EntryContext, args: {
  activityIds: string[];
  targetDayId: string;
  before: ItineraryDay[];
  copyIds: string[];
  message: string;
}): HistoryEntry {
  const { activityIds, targetDayId, before, message } = args;
  // Each redo makes new copies with new ids; the next undo has to delete those.
  let copyIds = args.copyIds;
  const continuations = continuationsOf(before);

  return {
    message,
    undo: async () => {
      await Promise.all(copyIds.map(id =>
        itineraryApi.deleteActivity(ctx.tripId, targetDayId, id).catch(e => { if (!isAlreadyGone(e)) throw e; }),
      ));
      await restoreContinuations(ctx, continuations);
      await reload(ctx);
    },
    redo: async () => {
      const had = new Set(stopIds(ctx.getDays().find(d => d.id === targetDayId)));
      const updated = await itineraryApi.duplicateActivities(ctx.tripId, activityIds, targetDayId);
      copyIds = stopIds(updated.find(d => d.id === targetDayId)).filter(id => !had.has(id));
      const byId = new Map(updated.map(d => [d.id, d]));
      ctx.setDays(prev => prev.map(d => byId.get(d.id) ?? d));
    },
  };
}

/**
 * ADR-0015: a delete is deferred. The cards leave the screen now; the DELETE
 * goes out in `commit`, when the undo window closes. Undo inside the window is
 * putting the cards back on screen — the server never lost them, so there is
 * no row to recreate and no cascade to reverse.
 */
export function deleteEntry(ctx: EntryContext, args: {
  activities: Activity[];
  before: ItineraryDay[];
  /** ADR-0024: the user said the linked meals go too. */
  withMeals?: boolean;
  message: string;
}): HistoryEntry {
  const { activities, before, withMeals = false, message } = args;
  const ids = activities.map(a => a.id);
  const removed = removeActivitiesLocally(before, ids);
  const touched = new Set(before.filter((d, i) => d !== removed[i]).map(d => d.id));

  return {
    message,
    undo: async () => {
      ctx.setDays(prev => prev.map(day => {
        if (!touched.has(day.id)) return day;
        const was = before.find(d => d.id === day.id);
        if (!was) return day;
        // Cards that stayed keep any edit made since; the removed ones come back as they were.
        const current = new Map(day.activities.map(a => [a.id, a]));
        return {
          ...day,
          startFromPrevStop: was.startFromPrevStop,
          activities: was.activities.map(a => current.get(a.id) ?? a),
        };
      }));
    },
    redo: async () => { ctx.setDays(prev => removeActivitiesLocally(prev, ids)); },
    commit: async () => {
      await Promise.all(activities.map(a =>
        itineraryApi.deleteActivity(ctx.tripId, a.dayId, a.id, withMeals && !!a.mealId)
          .catch(e => { if (!isAlreadyGone(e)) throw e; }),
      ));
    },
  };
}

export function legModeEntry(args: {
  apply: (mode: string | null) => Promise<void>;
  before: string | null;
  after: string | null;
  message: string;
}): HistoryEntry {
  const { apply, before, after, message } = args;
  return { message, undo: () => apply(before), redo: () => apply(after) };
}
